import { getMode } from '../../utils/functions';
import { DEFAULT_COUNTRY_ISO2 } from '../geo/geoConstants';

/*
 * The login answers with a different shape per profile: an admin brings
 * `admin`, a seller brings `seller_id` and `seller_company`. Reading those keys
 * straight from a page is how a screen ends up greeting a seller as "usuario".
 */

/** @returns {'admin'|'operator'|'seller'|null} */
export const selectRole = (state) => getMode(state.auth.user);

/** @returns {number|null} */
export const selectSellerId = (state) => state.auth.user?.seller_id ?? null;

/** @returns {number|null} The admin profile behind the session, which owns what it creates. */
export const selectAdminId = (state) => state.auth.user?.admin?.id ?? null;

/**
 * Whether the session belongs to the internal team.
 *
 * `is_staff` is Django's flag on the user, not one of the profiles `user_type`
 * lists: an account can be an admin and staff at once. It widens what a screen
 * offers — choosing which admin owns a shipping zone, for one — and never what
 * the backend accepts, which checks it again. See [[permisos-admin-vs-staff]].
 *
 * @returns {boolean}
 */
export const selectIsStaff = (state) => state.auth.user?.is_staff === true;

/**
 * Whether the account runs on the checkout plan.
 *
 * `plans.PLAN_TYPES` has exactly one of them — `admin_checkout`, "Admin Self
 * Checkout" — next to `admin_tms`, `seller_full` and `seller_limited`. It is
 * the plan that assigns drivers, so it is what gates everything about them:
 * the driver picker of an order, the massive change of driver, and the driver
 * filter of the listing. On any other plan those screens can only come back
 * empty.
 *
 * @returns {boolean}
 */
export const selectIsCheckoutPlan = (state) =>
  state.auth.user?.admin_plan?.plan_type === 'admin_checkout';

/**
 * Whether this session belongs somewhere else — and if so, where.
 *
 * Two profiles have no workspace in this app and a complete one in the front it
 * is replacing: an account on the checkout plan, and a driver. Until now the
 * first landed on the admin workspace's own 404 with the whole TMS sidebar
 * around it, and the second on a screen whose only button is "log out".
 *
 * Returns which of the two it is, not a boolean, because the handover screen
 * does not say the same thing to a driver as to an operator.
 *
 * **The order is not the old front's order, deliberately.** That one asks about
 * the driver first, which is safe there because every profile has a home. Here
 * it would be a regression: `user_type` is a list — the backend appends the
 * permission of *every* profile row a user has — so an admin who also has a
 * driver record arrives as `['admin', 'driver']`, and asking about the driver
 * first would eject an `admin_tms` that works perfectly well today. So a driver
 * only leaves when this app has nowhere to put them at all.
 *
 * Two things that deliberately do **not** leave:
 *
 * - **The internal team.** `is_staff` sits on top of an admin account that has
 *   a plan of its own, and putting an employee on the checkout plan is exactly
 *   how you would test checkout features. Ejecting them from the staff
 *   workspace over it is the worse failure.
 * - **A session with no plan.** `plan_type` is null whenever the admin's
 *   `AdminPlan` has no plan attached, which is most of them in development.
 *   Unknown is not checkout.
 *
 * `kam` is left out on purpose: the old front serves it too, but it is not what
 * this change was asked for, and adding it is one line here plus a line of
 * copy. The warehouse `operator` has its own workspace here.
 *
 * @param {object} user - Signed-in user from the auth slice.
 * @returns {'admin_checkout'|'driver'|null}
 */
export const legacyProfileOf = (user) => {
  if (!user || user.is_staff === true) return null;
  if (user.admin_plan?.plan_type === 'admin_checkout') return 'admin_checkout';
  if (user.user_type?.includes('driver') && getMode(user) === null) return 'driver';
  return null;
};

/** @returns {'admin_checkout'|'driver'|null} */
export const selectLegacyProfile = (state) => legacyProfileOf(state.auth.user);

/**
 * The addons of the plan in force for this session.
 *
 * A seller has no plan of its own: `AdminPlan` is one per admin, and the login
 * looks it up by the seller's `admin_id` and sends it as `seller_admin_plan`.
 * So the same addon lives under two different keys depending on who signed in,
 * and this is the only place that knows it.
 *
 * An account with no plan gets `{}`, which reads as every addon off — the same
 * thing the legacy does with an empty `planAddons`.
 *
 * @returns {object}
 */
export const selectPlanAddons = (state) =>
  state.auth.user?.admin_plan?.admin_permissions?.addons ||
  state.auth.user?.seller_admin_plan?.admin_permissions?.addons ||
  {};

/**
 * Fulfillment Pro: the courier of a shipment, its dispatch and the parcels it
 * is split into. Without it those are read-only facts.
 *
 * @returns {boolean}
 */
export const selectHasFulfillmentPro = (state) =>
  selectPlanAddons(state).fulfillment_pro === true;

/**
 * Whether the order lines can be picked one by one.
 *
 * Either fulfillment addon buys it — the condition the legacy writes as
 * `hasFulfillmentLite || hasFulfillmentPro`.
 *
 * @returns {boolean}
 */
export const selectCanPickOrderLines = (state) => {
  const addons = selectPlanAddons(state);
  return addons.fulfillment_lite === true || addons.fulfillment_pro === true;
};

/**
 * The warehouse addons in force for this session.
 *
 * Not `selectPlanAddons`: a seller's module is its admin's ceiling **crossed
 * with** what its own contract switched on, and the login already resolves that
 * into `company_addons`. Reading the admin's plan for a seller would show a
 * warehouse module the company never contracted.
 *
 * @returns {object}
 */
export const selectWmsAddons = (state) =>
  state.auth.user?.company_addons || selectPlanAddons(state);

/**
 * Whether the account has the warehouse module at all. Pro includes lite.
 *
 * The module has keys of its own, `wms_pro` / `wms_lite`. `fulfillment_*` is
 * another product — preparing an order from the OMS's order detail — and
 * switching it on never opens the warehouse.
 *
 * @returns {boolean}
 */
export const selectHasWmsAccess = (state) => {
  const addons = selectWmsAddons(state);
  return addons.wms_pro === true || addons.wms_lite === true;
};

/**
 * Pickup points: the operator's shops where a courier leaves a parcel for the
 * end customer to collect. An addon of the admin's plan, read like the general
 * ones: a seller inherits it from its admin's plan and only gets to see where
 * a shipment went; redirecting is the admin's.
 *
 * @returns {boolean}
 */
export const selectHasPickupPoints = (state) => selectPlanAddons(state).pickup_points === true;

/**
 * Filtro avanzado of the deliveries listing: one criterion and a pasted list
 * of values. An addon of the admin's plan that, unlike the general ones, a
 * seller does **not** inherit: only the admin's own plan counts, never
 * `seller_admin_plan`. The backend refuses the parameters to anyone else,
 * which is why the pills only show up when this says so.
 *
 * @returns {boolean}
 */
export const selectHasAdvancedFilter = (state) =>
  state.auth.user?.admin_plan?.admin_permissions?.addons?.advanced_filter === true;

const NO_PERMISSIONS = [];

/**
 * What the role may do inside the warehouse module, as the login sends it:
 * `stock:read`, `inventory:putaway`, `order:fulfill`...
 *
 * The server decides and checks again on every call; this only keeps a screen
 * from drawing a control that would answer 403.
 *
 * @returns {string[]}
 */
export const selectWmsPermissions = (state) =>
  state.auth.user?.wms_permissions || NO_PERMISSIONS;

/**
 * @param {string} permission - e.g. `'inventory:putaway'`.
 * @returns {(state: object) => boolean}
 */
export const selectCanWms = (permission) => (state) =>
  selectWmsPermissions(state).includes(permission);

/**
 * The country the account operates in: an admin carries it on its own profile,
 * a seller on its company. Both models also have a numeric `country`; only
 * `country_fk` is authoritative.
 *
 * @param {object} user - Signed-in user from the auth slice
 * @returns {string} ISO2 country code
 */
export const getUserCountryIso2 = (user) =>
  user?.admin?.country_fk?.iso2 ||
  user?.seller_company?.country_fk?.iso2 ||
  DEFAULT_COUNTRY_ISO2;

export const selectCountryIso2 = (state) => getUserCountryIso2(state.auth.user);

/** @returns {string} */
export const selectDisplayName = (state) => {
  const user = state.auth.user;
  if (!user) return '';

  const fromAdminProfile = [user.admin?.first_name, user.admin?.last_name]
    .filter(Boolean)
    .join(' ');

  return user.full_name || fromAdminProfile || 'usuario';
};
